import { type MouseEvent, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useLoginMutation,
  useLazyGetUserPermissionsQuery,
} from '@/services/AuthService';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KeenIcon } from '@/components';
import { Alert } from '@/components';
import { useDispatch } from 'react-redux';
import {
  loginReducer,
  setProfilePic,
  setUserData,
  setUserType,
  setUserPermissions,
} from '@/store/authSlice';
import { useSnackbar } from 'notistack';
import { emailValidation, loginPasswordValidation } from '@/utils/validations';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import { UserRoles } from '@/constant';
import { getFirstAllowedRoute } from '@/constant/rolePermissions';
import { LoginResponse } from '@/types/auth.types';
import { initializeRolePermissions } from '@/services/RolePermissionService';

const loginSchema = Yup.object().shape({
  email: emailValidation(),
  password: loginPasswordValidation(),
});

const initialValues = {
  email: '',
  password: '',
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [login] = useLoginMutation();
  const [getUserPermissions] = useLazyGetUserPermissionsQuery();
  const dispatch = useDispatch();
  const errorRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const prevStatusRef = useRef<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const redirectAfterLogin =
    typeof location.state?.from?.pathname === 'string'
      ? `${location.state.from.pathname}${location.state.from.search || ''}${location.state.from.hash || ''}`
      : null;

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        setLoading(true);
        const response: LoginResponse = await login({
          email: values.email,
          password: values.password,
          isAdminPanel: true,
        }).unwrap();

        if (response.status) {
          if (!response.data.roles || response.data.roles?.length === 0) {
            localStorage.setItem(
              'message',
              'It looks like no roles are assigned to your account yet. Please reach out to platform administrator to request the right access.'
            );
            navigate('/error/401');
            return;
          }
          dispatch(setUserData({ userData: response.data }));
          dispatch(loginReducer({ token: response.data.token }));
          dispatch(setUserType({ user_type: response.data.user_type }));
          dispatch(
            setProfilePic({ profile_picture: response.data.profile_picture })
          );

          try {
            const permissionsResponse = await getUserPermissions({
              lang: 'en',
            }).unwrap();

            if (permissionsResponse && permissionsResponse.length > 0) {
              const allRolePermissions = permissionsResponse.flatMap(
                (role) => role.role_has_permissions
              );

              dispatch(
                setUserPermissions({
                  permissions: allRolePermissions,
                })
              );

              // Initialize dynamic role permissions from API response
              initializeRolePermissions(permissionsResponse);
            }
          } catch (permError) {
            console.error('Failed to fetch user permissions:', permError);
          }

          enqueueSnackbar(response.message || 'Login successful', {
            variant: 'solid',
            state: 'success',
          });
          if (redirectAfterLogin) {
            navigate(redirectAfterLogin, { replace: true });
            return;
          }
          const userType = response.data.roles;
          const redirectPath = getFirstAllowedRoute(userType as UserRoles[]);
          navigate(redirectPath, { replace: true });
        } else {
          throw new Error(response.message);
        }
      } catch (error: unknown) {
        function hasDataWithMessage(
          err: unknown
        ): err is { data: { message?: string } } {
          return (
            typeof err === 'object' &&
            err !== null &&
            'data' in err &&
            typeof (err as { data: { message?: string } }).data === 'object'
          );
        }

        const errorMessage = hasDataWithMessage(error)
          ? error.data.message || 'The login details are incorrect'
          : 'The login details are incorrect';

        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, {
          variant: 'solid',
          state: 'danger',
        });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  // Focus the form-level error alert only when status changes to a new value
  useEffect(() => {
    if (formik.status && formik.status !== prevStatusRef.current) {
      errorRef.current?.focus();
    }
    prevStatusRef.current = (formik.status as string) ?? null;
  }, [formik.status]);

  // If there's no form-level status, manage field-level focus for validation errors
  useEffect(() => {
    if (!formik.status) {
      if (formik.touched.email && formik.errors.email) {
        emailInputRef.current?.focus();
      } else if (formik.touched.password && formik.errors.password) {
        passwordInputRef.current?.focus();
      }
    }
  }, [
    formik.status,
    formik.touched.email,
    formik.errors.email,
    formik.touched.password,
    formik.errors.password,
  ]);

  const togglePassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowPassword(!showPassword);
  };

  return (
    <div className="card max-w-[390px] w-full contrast:!bg-card">
      <form
        aria-label="Sign in to your account"
        aria-busy={loading}
        className="card-body flex flex-col gap-5 p-10"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className="text-center mb-2.5">
          <h1 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
            Sign in
          </h1>
        </div>

        {formik.status && (
          <div ref={errorRef} tabIndex={-1} aria-live="polite">
            <Alert variant="danger">{formik.status}</Alert>
          </div>
        )}

        {/* Email field */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="email"
            className="form-label text-gray-900"
            aria-label="Email"
          >
            Email
          </label>
          <input
            ref={emailInputRef}
            id="email"
            type="email"
            placeholder="Enter email address"
            autoComplete="username"
            aria-invalid={
              formik.touched.email && formik.errors.email ? 'true' : 'false'
            }
            aria-describedby={
              formik.touched.email && formik.errors.email
                ? 'email-error'
                : undefined
            }
            {...formik.getFieldProps('email')}
            required
            aria-required="true"
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              input.value = input.value.replace(/\s/g, '');
              formik.setFieldValue('email', input.value);
            }}
            className={clsx(
              'form-control input !text-gray-900 placeholder:!text-gray-800',
              {
                'is-invalid': formik.touched.email && formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <span
              role="alert"
              className="text-danger contrast:text-red-500 text-xs mt-1"
              id="email-error"
            >
              {formik.errors.email}
            </span>
          )}
        </div>

        {/* Password field */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="password"
            className="form-label text-gray-900"
            aria-label="Password"
          >
            Password
          </label>
          <div className="relative">
            <input
              ref={passwordInputRef}
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter password"
              autoComplete="current-password"
              aria-invalid={
                formik.touched.password && formik.errors.password
                  ? 'true'
                  : 'false'
              }
              aria-describedby={
                formik.touched.password && formik.errors.password
                  ? 'password-error'
                  : undefined
              }
              {...formik.getFieldProps('password')}
              required
              aria-required="true"
              className={clsx(
                'form-control input pr-10 !text-gray-900 placeholder:!text-gray-800',
                {
                  'is-invalid':
                    formik.touched.password && formik.errors.password,
                }
              )}
              onKeyDown={preventLeadingWhitespace}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-icon"
              onClick={togglePassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <KeenIcon
                icon="eye"
                className={clsx('text-gray-500', { hidden: showPassword })}
                aria-hidden="true"
              />
              <KeenIcon
                icon="eye-slash"
                className={clsx('text-gray-500', { hidden: !showPassword })}
                aria-hidden="true"
              />
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <span
              role="alert"
              className="text-danger contrast:text-red-500 text-xs mt-1"
              id="password-error"
            >
              {formik.errors.password}
            </span>
          )}
        </div>

        <div className="flex justify-end -mt-1">
          <Link to="/auth/reset-password" className="text-sm font-medium link">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          className="btn btn-primary flex justify-center grow"
          disabled={loading || formik.isSubmitting}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};

export { Login };
